/**
 * @author       matthew@zumper.com (Matthew Talamini)
 * @copyright    Copyright (c) 2021, Zumper
 * @description
 */

import {
  selectCurrentKey,
  selectError,
  selectFetching,
  selectFetchingKey,
} from '@zcode/nr/es/shared/modules/meta/selectors'
import { dollarize } from '@zcode/utils'
import { compose } from 'redux'
import { selectFilterEndDate, selectFilterStartDate } from '../../filters/selectors/index'

export * from './vrbo'

/**
 *
 * @param {import('@zcode/state-type').State} state
 */
export const selectShortTermAvailability = (state) => state.shortTermAvailability

export const selectShortTermAvailabilityData = compose(
  (state) => state.shortTermAvailability,
  selectShortTermAvailability
)

export const selectShortTermAvailabilityEndDate = compose(
  (state) => state.end_date,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityStartDate = compose(
  (state) => state.start_date,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityStatus = compose(
  (state) => state.status,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityDeposit = compose(
  (state) => state.deposit,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityFee = compose(
  (state) => state.fee,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityMaxGuests = compose(
  (state) => state.max_guests,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityMaxLength = compose(
  (state) => state.max_length_of_stay,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityMinLength = compose(
  (state) => state.min_length_of_stay,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityTotalPrice = compose(
  (state) => state.total_price,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityPrice = compose(
  (state) => state.price,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityDepositText = (state) => {
  const deposit = selectShortTermAvailabilityDeposit(state)
  return deposit ? dollarize(deposit / 100) : undefined
}

export const selectShortTermAvailabilityFeeText = (state) => {
  const fee = selectShortTermAvailabilityFee(state)
  return fee ? dollarize(fee / 100) : undefined
}

export const selectShortTermAvailabilityTotalPriceText = (state) => {
  const totalPrice = selectShortTermAvailabilityTotalPrice(state)
  return totalPrice ? dollarize(totalPrice / 100) : undefined
}

export const selectShortTermAvailabilityPerNightPriceText = (state) => {
  const price = selectShortTermAvailabilityPrice(state)
  return price ? dollarize(price / 100) : undefined
}

export const selectShortTermAvailabilityIsAvailable = compose(
  (state) => state.is_available,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityExternalLink = compose(
  (state) => state.external_link,
  selectShortTermAvailabilityData
)

export const selectShortTermAvailabilityExternalLinkWithDates = (state) => {
  const startDate = selectFilterStartDate(state)
  const endDate = selectFilterEndDate(state)
  const externalLink = selectShortTermAvailabilityExternalLink(state)
  if (startDate && endDate) {
    return `${externalLink}?start-date=${startDate}&end-date=${endDate}`
  }
  return externalLink
}

// meta
export const selectShortTermAvailabilityMeta = compose(
  (state) => state.meta,
  selectShortTermAvailability
)

export const selectShortTermAvailabilityErrorError = compose(
  selectError,
  selectShortTermAvailabilityMeta
)

export const selectShortTermAvailabilityCurrentKey = compose(
  selectCurrentKey,
  selectShortTermAvailabilityMeta
)

export const selectShortTermAvailabilityFetching = compose(
  selectFetching,
  selectShortTermAvailabilityMeta
)

export const selectShortTermAvailabilityFetchingKey = compose(
  selectFetchingKey,
  selectShortTermAvailabilityMeta
)
